import { getSupabaseBrowserClient } from './supabase'
import { readFunctionError as readEdgeFunctionError } from './function-error'

export const organisationRoleCodes = [
  'organisation_admin',
  'governance_manager',
  'use_case_owner',
  'technical_owner',
  'reviewer',
  'approver',
  'executive_viewer',
] as const

export type OrganisationRoleCode = (typeof organisationRoleCodes)[number]

// UAT-04F3: the active-member editor offers only the current Release 1
// Organisation Administrator capability. Legacy role codes remain valid read
// values so historical/current compatibility facts can still be displayed.
export const peopleAccessRoleCodes = ['organisation_admin'] as const

export type PeopleAccessRoleCode =
  | 'organisation_admin'
  | 'governance_manager'
  | 'executive_viewer'

export const organisationRoleLabels: Record<OrganisationRoleCode, string> = {
  organisation_admin: 'Organisation Administrator',
  governance_manager: 'Governance Manager',
  use_case_owner: 'Use Case Owner',
  technical_owner: 'Technical Owner',
  reviewer: 'Reviewer',
  approver: 'Approver',
  executive_viewer: 'Executive Viewer',
}

export type UserProfile = {
  userId: string
  displayName: string
  status: string
  initialPasswordSetAt: string | null
}

export type OrganisationSummary = {
  id: string
  name: string
  sector: string | null
  sizeBand: string | null
  countryCode: string
  onboardingStatus: string
}

export type OrganisationAccess = {
  membershipId: string
  status: string
  roles: OrganisationRoleCode[]
  organisation: OrganisationSummary
}

export type OrganisationMember = {
  membershipId: string
  userId: string
  status: string
  displayName: string
  email: string
  roles: PeopleAccessRoleCode[]
}

export type OrganisationInvitation = {
  id: string
  displayName: string
  email: string
  status: string
  invitedAt: string
  expiresAt: string
  roles: PeopleAccessRoleCode[]
}

type InvitationFunctionResponse = {
  invitationId?: string
  delivery?: string
  expiresAt?: string
  warning?: string
  error?: string
}

function throwIfError(error: { message: string } | null) {
  if (error) {
    throw new Error(error.message)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

async function readFunctionError(value: unknown): Promise<string | null> {
  if (value === null || value === undefined) {
    return null
  }

  return readEdgeFunctionError(value, 'Invitation service request failed.')
}

function readInvitationFunctionData(value: unknown): InvitationFunctionResponse | null {
  if (value === null || value === undefined) {
    return null
  }

  if (!isRecord(value)) {
    throw new Error('Invitation service returned an invalid response.')
  }

  return {
    invitationId: typeof value.invitationId === 'string' ? value.invitationId : undefined,
    delivery: typeof value.delivery === 'string' ? value.delivery : undefined,
    expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : undefined,
    warning: typeof value.warning === 'string' ? value.warning : undefined,
    error: typeof value.error === 'string' ? value.error : undefined,
  }
}

async function invokeInvitationFunction(body: Record<string, unknown>) {
  const supabase = getSupabaseBrowserClient()
  const rawResponse: unknown = await supabase.functions.invoke<InvitationFunctionResponse>(
    'invite-organisation-user',
    { body },
  )

  if (!isRecord(rawResponse)) {
    throw new Error('Invitation service returned an invalid response envelope.')
  }

  const errorMessage = await readFunctionError(rawResponse.error)

  if (errorMessage) {
    throw new Error(errorMessage)
  }

  const data = readInvitationFunctionData(rawResponse.data)

  if (!data) {
    throw new Error('Invitation service returned no response.')
  }

  if (data.error) {
    throw new Error(data.error)
  }

  if (!data.invitationId || !data.delivery) {
    throw new Error('Invitation service returned an incomplete response.')
  }

  return {
    invitationId: data.invitationId,
    delivery: data.delivery,
    expiresAt: data.expiresAt,
    warning: data.warning,
  }
}

export async function claimPendingOrganisationInvitations() {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('claim_pending_organisation_invitations')
  throwIfError(error)
  return Number(data ?? 0)
}

export async function loadUserProfile(userId: string): Promise<UserProfile | null> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, display_name, status, initial_password_set_at')
    .eq('user_id', userId)
    .maybeSingle()

  throwIfError(error)

  if (!data) {
    return null
  }

  return {
    userId: data.user_id,
    displayName: data.display_name,
    status: data.status,
    initialPasswordSetAt: data.initial_password_set_at,
  }
}

export async function updateUserProfile(displayName: string) {
  const supabase = getSupabaseBrowserClient()
  const { data: userData, error: userError } = await supabase.auth.getUser()
  throwIfError(userError)

  if (!userData.user) {
    throw new Error('Authentication required')
  }

  const { error } = await supabase
    .from('user_profiles')
    .update({
      display_name: displayName.trim(),
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userData.user.id)

  throwIfError(error)
}

export async function loadOrganisationAccess(userId: string): Promise<OrganisationAccess[]> {
  const supabase = getSupabaseBrowserClient()
  const { data: memberships, error: membershipError } = await supabase
    .from('organisation_memberships')
    .select('id, organisation_id, status')
    .eq('user_id', userId)
    .eq('status', 'active')

  throwIfError(membershipError)

  if (!memberships || memberships.length === 0) {
    return []
  }

  const organisationIds = [...new Set(memberships.map((membership) => membership.organisation_id))]
  const membershipIds = memberships.map((membership) => membership.id)

  const [{ data: organisations, error: organisationError }, { data: roleRows, error: roleError }] =
    await Promise.all([
      supabase
        .from('organisations')
        .select('id, name, sector, size_band, country_code, onboarding_status')
        .in('id', organisationIds),
      supabase
        .from('organisation_membership_roles')
        .select('membership_id, role_code, revoked_at')
        .in('membership_id', membershipIds),
    ])

  throwIfError(organisationError)
  throwIfError(roleError)

  const organisationMap = new Map((organisations ?? []).map((organisation) => [organisation.id, organisation]))
  const rolesByMembership = new Map<string, OrganisationRoleCode[]>()

  for (const roleRow of roleRows ?? []) {
    if (roleRow.revoked_at) {
      continue
    }

    const currentRoles = rolesByMembership.get(roleRow.membership_id) ?? []
    currentRoles.push(roleRow.role_code as OrganisationRoleCode)
    rolesByMembership.set(roleRow.membership_id, currentRoles)
  }

  return memberships.map((membership) => {
    const organisation = organisationMap.get(membership.organisation_id)

    if (!organisation) {
      throw new Error('An active organisation membership has no readable organisation record.')
    }

    return {
      membershipId: membership.id,
      status: membership.status,
      roles: rolesByMembership.get(membership.id) ?? [],
      organisation: {
        id: organisation.id,
        name: organisation.name,
        sector: organisation.sector,
        sizeBand: organisation.size_band,
        countryCode: organisation.country_code,
        onboardingStatus: organisation.onboarding_status,
      },
    }
  })
}

export async function createOrganisation(input: {
  name: string
  sector?: string
  sizeBand?: string
}) {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('create_organisation', {
    p_name: input.name,
    p_sector: input.sector ?? null,
    p_size_band: input.sizeBand ?? null,
  })

  throwIfError(error)
  return data
}

export async function updateOrganisation(input: {
  organisationId: string
  name: string
  sector?: string
  sizeBand?: string
}) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('update_organisation_profile', {
    p_organisation_id: input.organisationId,
    p_name: input.name,
    p_sector: input.sector ?? null,
    p_size_band: input.sizeBand ?? null,
  })

  throwIfError(error)
}

export async function loadOrganisationMembers(
  organisationId: string,
): Promise<OrganisationMember[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('get_organisation_people_access', {
    p_organisation_id: organisationId,
  })
  throwIfError(error)
  return ((data as { members?: OrganisationMember[] } | null)?.members ?? [])
}

export async function loadOrganisationInvitations(
  organisationId: string,
): Promise<OrganisationInvitation[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('get_organisation_people_access', {
    p_organisation_id: organisationId,
  })
  throwIfError(error)
  return ((data as { invitations?: OrganisationInvitation[] } | null)?.invitations ?? [])
}

export async function setOrganisationMembershipRoles(
  membershipId: string,
  roles: OrganisationRoleCode[],
) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_membership_roles', {
    p_membership_id: membershipId,
    p_role_codes: roles,
  })
  throwIfError(error)
}

export async function setOrganisationPeopleAccessRoles(
  membershipId: string,
  roles: PeopleAccessRoleCode[],
) {
  const supabase = getSupabaseBrowserClient()
  // Only current Release 1 administration is writable from the simple editor.
  // Legacy values may still be present in the read model but are deliberately
  // omitted from the write payload so the server can preserve them unchanged.
  const editableRoles = roles.includes('organisation_admin') ? ['organisation_admin'] : []
  const { error } = await supabase.rpc('set_organisation_people_access_roles', {
    p_membership_id: membershipId,
    p_role_codes: editableRoles,
  })
  throwIfError(error)
}

export async function setOrganisationMembershipStatus(
  membershipId: string,
  status: 'active' | 'suspended' | 'revoked',
) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('set_organisation_membership_status', {
    p_membership_id: membershipId,
    p_status: status,
  })
  throwIfError(error)
}

export async function inviteOrganisationUser(input: {
  organisationId: string
  displayName?: string
  email: string
  roles: OrganisationRoleCode[]
}) {
  return invokeInvitationFunction({
    action: 'invite',
    organisationId: input.organisationId,
    displayName: input.displayName,
    email: input.email,
    roles: input.roles,
  })
}

export async function resendOrganisationInvitation(invitationId: string) {
  return invokeInvitationFunction({
    action: 'resend',
    invitationId,
  })
}

export async function cancelOrganisationInvitation(invitationId: string) {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('revoke_organisation_invitation', {
    p_invitation_id: invitationId,
  })
  throwIfError(error)
}

export async function loadMyPlatformCapabilities(): Promise<string[]> {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('get_my_platform_capabilities')
  throwIfError(error)
  return (data ?? []).map((row) => row.capability_code)
}
